import React, { useEffect, useRef, useState } from 'react';
import { Alert, AppLayout, Box, ContentLayout, Header, SpaceBetween, Spinner, Tabs, TabsProps } from '@amzn/awsui-components-core';
import { Dashboard } from '@amzn/cloudwatchdashboards-inside';
import { DashboardConfig, fetchDashboardConfigs } from 'src/services/DashboardsService';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';
import { OESideNavigation } from 'src/components/fintech-ops/fintech-ops-dashboard/OESideNavigation';

const CW_REGION = 'us-west-2';
const READY_TIMEOUT_MS = 15_000;

const getDashboardConfig = () => ({
  heightUnit: 40,
  displayMode: 'static' as const,
  widgetMarginX: 10,
  widgetMarginY: 10,
  style: 'polarisCard' as const,
  embeddedMaximize: true,
  internal: {
    accountId: ENVIRONMENT_VARIABLES.env.AwsAccountId,
    stage: 'prod'
  }
});

const IFRAME_STYLE = { width: '100%', height: '100%', border: 'none' };

/** Renders a single embedded CloudWatch dashboard */
const EmbeddedDashboard: React.FC<{ dashboardName: string }> = ({ dashboardName }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!containerRef.current) return;

    const cw = new Dashboard(containerRef.current, dashboardName, CW_REGION, getDashboardConfig(), IFRAME_STYLE);

    cw.on('ready', () => setLoading(false));
    cw.load(dashboardName);

    const timeout = setTimeout(() => setLoading(false), READY_TIMEOUT_MS);

    return () => {
      clearTimeout(timeout);
      cw.release();
    };
  }, [dashboardName]);

  return (
    <div style={{ position: 'relative', minHeight: 600, height: 'calc(100vh - 220px)' }} aria-label={`CloudWatch dashboard: ${dashboardName}`}>
      {loading && (
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
        </Box>
      )}
      <div ref={containerRef} style={{ height: '100%' }} />
    </div>
  );
};

/** KPI Metrics page — dynamically loads dashboards from DynamoDB registry */
export const KpiMetricsPage: React.FC = () => {
  const [dashboards, setDashboards] = useState<DashboardConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTabId, setActiveTabId] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const configs = await fetchDashboardConfigs();
        if (cancelled) return;
        const aggregated = configs.filter((d) => d.dashboardId.startsWith('aggregated'));
        setDashboards(aggregated);
        if (aggregated.length > 0) setActiveTabId(aggregated[0].dashboardId);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppLayout
      navigation={<OESideNavigation />}
      toolsHide={true}
      maxContentWidth={Number.MAX_VALUE}
      content={
        <ContentLayout>
          <SpaceBetween size="m">
            <Header variant="h1" description="Embedded CloudWatch dashboards for monitoring and analytics across DaS Fintech portals">
              KPI Metrics
            </Header>

            {loading ? (
              <Box textAlign="center" padding="xxl">
                <Spinner size="large" />
              </Box>
            ) : error ? (
              <Alert type="error" header="Failed to load dashboards">
                An error occurred while loading dashboard configurations. Try refreshing the page.
              </Alert>
            ) : dashboards.length === 0 ? (
              <Box textAlign="center" padding="xxl">
                No dashboards configured.
              </Box>
            ) : (
              <Tabs
                activeTabId={activeTabId}
                onChange={({ detail }: { detail: TabsProps.ChangeDetail }) => setActiveTabId(detail.activeTabId)}
                tabs={dashboards.map((d) => ({
                  id: d.dashboardId,
                  label: d.displayName,
                  content: <EmbeddedDashboard dashboardName={d.dashboardName} />
                }))}
              />
            )}
          </SpaceBetween>
        </ContentLayout>
      }
    />
  );
};

export default KpiMetricsPage;
